import { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  en: {
    // Navigation
    nav: {
      dashboard: 'Dashboard',
      cases: 'Cases',
      documents: 'Documents',
      timeTracking: 'Time Tracking',
      billing: 'Billing',
      trust: 'Trust/IOLTA',
      tasks: 'Tasks',
      calendar: 'Calendar',
      clients: 'Clients',
      clientPortal: 'Client Portal',
      aiAssistant: 'AI Assistant',
      templates: 'Templates',
      integrations: 'Integrations',
      help: 'Help Center',
      team: 'Team',
      adminPortal: 'Admin Portal',
      affiliates: 'Affiliates',
      auditLog: 'Audit Log',
      settings: 'Settings',
      signOut: 'Sign Out',
      signIn: 'Sign In',
      getStarted: 'Get Started'
    },
    // Landing Page
    landing: {
      badge: 'Legal Practice Management for Dominican Firms',
      heroTitle: 'Your Billable Hours',
      heroSubtitle: 'Deserve Better Than a Spreadsheet',
      heroDescription: 'Cases, documents, billing, trust accounts, and legal research — finally in one place. Built for the way Dominican law firms actually work.',
      startTrial: 'Start 14-day trial',
      watchDemo: 'Chat on WhatsApp',
      lawFirmsTrust: 'firms manage their practice with ERP Legal',
      moreBillableHours: 'More Billable Hours',
      fasterRetrieval: 'Find Any Document in Seconds',
      trustCompliance: 'Trust Accounts That Balance',
      reducedAdminTime: 'Less Paperwork, More Law',
      challengesSolved: 'Sound Familiar?',
      challengesSubtitle: 'Every hour spent chasing files, reconciling spreadsheets, or rewriting the same contract is an hour you can\'t bill. These are the problems we set out to fix.',
      everythingNeeds: 'One Platform, Every Tool',
      everythingSubtitle: 'From intake to invoice — the day-to-day tools your team actually uses, without the bloat.',
      simplePricing: 'One Plan. No Surprises.',
      pricingSubtitle: 'Flat per-seat pricing with everything included. Scale up with add-ons only when you need them.',
      readyTransform: 'Your Firm Runs on Time. So Should Your Software.',
      ctaDescription: 'Stop toggling between five apps to do what one should handle. Cases, calendar, billing, trust, AI — give your team a single place to work.',
      scheduleDemo: 'Chat on WhatsApp',
      perMonth: '/month',
      perUser: 'Per user, billed annually',
      contactUs: 'Contact us for pricing',
      contactSales: 'Contact Sales',
      mostPopular: 'Most Popular',
      // Before/After comparison section
      beforeAfterTitle: 'Your Firm Today vs. With ERP Legal',
      beforeAfterSubtitle: 'A quick look at what actually changes day-to-day.',
      beforeLabel: 'Without ERP Legal',
      afterLabel: 'With ERP Legal',
      before1: 'Rewriting the same contract from scratch every week',
      after1: 'Templates with smart fill — draft in minutes, not hours',
      before2: 'Billable hours jotted on a sticky note, half of them forgotten',
      after2: 'One-click timer on every case, invoices build themselves',
      before3: 'Clients texting at 9 PM asking "any news on my case?"',
      after3: 'A portal where they check status on their own',
      before4: 'Deadlines split across five calendars and a WhatsApp group',
      after4: 'One calendar with alerts before anything slips through',
      before5: 'Trust reconciliation takes two days a month and a bottle of aspirin',
      after5: 'Balances update in real time — always to the cent',
      before6: '"Who has the latest version of the brief?"',
      after6: 'One file, one version, full history',
      // Outcome band
      outcomeTitle: 'Results you can measure',
      outcomeSubtitle: 'Here\'s what firms tell us after their first 90 days.'
    },
    // Features
    features: {
      caseManagement: 'Cases',
      caseDesc: 'Every case in one place — files, notes, contacts, timeline. No more digging through folders.',
      timeTracking: 'Time Tracking',
      timeDesc: 'Start a timer or log hours manually. Billable time goes straight to the invoice.',
      billing: 'Billing',
      billingDesc: 'Build invoices from logged time, send them by email, track who paid and who didn\'t.',
      trust: 'Trust / IOLTA',
      trustDesc: 'Track every deposit, withdrawal, and transfer. Balances reconcile automatically.',
      calendar: 'Calendar & Tasks',
      calendarDesc: 'Hearings, deadlines, meetings — all in one view with alerts so nothing falls through.',
      ai: 'AI Legal Assistant',
      aiDesc: 'Ask questions about Dominican law, summarize long documents, draft clauses — in seconds.',
      clientPortal: 'Client Portal',
      clientPortalDesc: 'Clients log in to see their case status, documents, and invoices without calling you.'
    },
    // Challenges
    challenges: {
      scattered: 'Case files live in WhatsApp, email, and a shared drive. Nobody knows which version is the right one.',
      scatteredSolution: 'One place for every case — with version history so you always know what\'s current.',
      lostHours: 'Lawyers forget to log time. By the end of the month, a big chunk of billable work was never recorded.',
      lostHoursSolution: 'A timer on every case. Tap once to start, and the hours show up on the invoice.',
      compliance: 'Trust account reconciliation done by hand, one spreadsheet error away from a bar complaint.',
      complianceSolution: 'Balances update with every transaction. Reconciliation happens in real time, not once a month.',
      deadlines: 'Court dates in one calendar, internal deadlines in another. Miss one hearing and you have a problem.',
      deadlinesSolution: 'One calendar for everything — with alerts that fire before it\'s too late.',
      communication: 'Clients call at 9 PM because there\'s no other way to check on their case.',
      communicationSolution: 'A secure portal where they see status, documents, and invoices on their own.',
      manualBilling: 'Putting together an invoice takes hours and one typo can cost you credibility.',
      manualBillingSolution: 'Invoices build from your time entries. PDF, email, dual-currency DOP/USD — one click.'
    },
    // Pricing (unified plan + add-ons)
    pricing: {
      planName: 'ERP Legal Unified',
      planTagline: 'Everything your firm needs, in one flat rate',
      pricePerUser: '$20',
      pricePerUserPerMonth: '/user/month',
      monthly: 'Monthly',
      annual: 'Annual',
      annualSave: 'Save 20%',
      annualBadge: '−20%',
      usersLabel: 'Number of users',
      usersHint: 'You can add or remove users anytime.',
      basePerUserMonthly: '$20 per user / month',
      basePerUserAnnual: '$192 per user / year (20% off)',
      effectivePerUserMonth: 'Effective rate',
      subtotalMonthly: 'Monthly total',
      subtotalAnnual: 'Annual total',
      billedMonthly: 'billed monthly',
      billedAnnually: 'billed once a year',
      youSave: 'You save',
      includedTitle: 'Included on every seat',
      included: [
        'Unlimited users',
        'Unlimited active cases',
        '500 AI legal assistant queries / month',
        '6 GB document storage',
        'Time tracking + billing',
        'IOLTA trust accounting',
        'Case, task & calendar management',
        'Dominican document templates',
        'Custom branding on invoices',
        'PDF + Word (.docx) exports',
        'Multi-currency (DOP/USD) with FX history',
        'Client portal + 2FA',
        'Priority email + chat support',
      ],
      addonsTitle: 'Grow on demand with add-ons',
      addonsSubtitle: 'Need more AI queries or storage? Stack the exact amount you need — cancel anytime.',
      aiAddonsTitle: 'AI query packs',
      storageAddonsTitle: 'Storage packs',
      aiAddonLabel: '+{n} queries / month',
      storageAddonLabel: '+{n} GB storage',
      perMonth: '/month',
      startTrial: 'Start 14-day free trial',
      cardRequiredNote: 'Card required to start the trial. Cancel anytime before day 14 at no charge.',
    },
    // Auth
    auth: {
      welcomeBack: 'Welcome back',
      createAccount: 'Create your account',
      enterCredentials: 'Sign in to pick up where you left off',
      startManaging: 'Set up your firm in less than two minutes',
      fullName: 'Full Name',
      lawFirmName: 'Law Firm Name',
      email: 'Email',
      password: 'Password',
      signInBtn: 'Sign In',
      createAccountBtn: 'Create Account',
      orContinueWith: 'Or continue with',
      continueGoogle: 'Continue with Google',
      noAccount: "Don't have an account?",
      haveAccount: 'Already have an account?',
      signUp: 'Sign up',
      backHome: '← Back to home',
      quote: '"The first duty of society is justice."',
      quoteAuthor: '— Alexander Hamilton'
    },
    // Dashboard
    dashboard: {
      welcomeBack: 'Welcome back',
      hereIsWhatsHappening: "Here's what's happening with your practice today.",
      newCase: 'New Case',
      activeCases: 'Active Cases',
      billableHours: 'Billable Hours',
      revenueMTD: 'Revenue MTD',
      pendingTasks: 'Pending Tasks',
      trustBalance: 'Trust Balance',
      ofTotal: 'of {total} total',
      paid: 'paid',
      quickActions: 'Quick Actions',
      startTimer: 'Start Timer',
      trackBillable: 'Track billable hours',
      addDocument: 'Add Document',
      uploadFiles: 'Upload case files',
      createInvoice: 'Create Invoice',
      billClients: 'Bill your clients',
      addTask: 'Add Task',
      trackTodo: 'Track to-do items',
      thisMonth: 'This Month',
      closedCases: 'Closed Cases',
      totalHours: 'Total Hours',
      draftInvoices: 'Draft Invoices',
      pendingInvoices: 'Pending Invoices',
      completedTasks: 'Completed Tasks',
      createOrganization: 'Create Organization',
      welcomeToLegalSuite: 'Welcome to ERP Legal!',
      toGetStarted: 'To get started, create your law firm organization. This will be your workspace for managing cases, documents, and billing.',
      createYourOrg: 'Create Your Organization'
    },
    // Cases
    cases: {
      title: 'Cases',
      subtitle: 'Manage your legal cases and matters',
      newCase: 'New Case',
      editCase: 'Edit Case',
      createNewCase: 'Create new case',
      caseTitle: 'Case title',
      caseTitlePlaceholder: 'Smith v. Johnson',
      clientName: 'Client name',
      clientNamePlaceholder: 'John Smith',
      clientEmail: 'Client email',
      clientEmailPlaceholder: 'client@email.com',
      caseType: 'Case type',
      selectType: 'Select type',
      status: 'Status',
      description: 'Description',
      descriptionPlaceholder: 'Case details and notes...',
      searchCases: 'Search cases...',
      allStatus: 'All status',
      open: 'Open',
      pending: 'Pending',
      closed: 'Closed',
      archived: 'Archived',
      noCases: 'No cases yet',
      emptyMessage: 'Open your first case to start tracking clients, documents, time and invoices in one place.',
      noMatchesTitle: 'No cases match your filters',
      noMatchesMessage: 'Try adjusting your search or status filter.',
      clearFilters: 'Clear filters',
      errTitleShort: 'Please enter at least 3 characters.',
      errClientRequired: 'Client name is required.',
      errEmailInvalid: 'Please enter a valid email address.',
      errTypeRequired: 'Please pick a case type.',
      createCase: 'Create case',
      updateCase: 'Update case',
      case: 'Case',
      client: 'Client',
      type: 'Type',
      date: 'Date',
      caseCreated: 'Case created',
      caseUpdated: 'Case updated',
      caseDeleted: 'Case deleted',
      confirmDelete: 'Are you sure you want to delete this case?',
      deleteFailed: 'Failed to delete case',
      operationFailed: 'Operation failed',
      // Case types
      civilLitigation: 'Civil Litigation',
      criminalDefense: 'Criminal Defense',
      familyLaw: 'Family Law',
      corporate: 'Corporate',
      realEstate: 'Real Estate',
      intellectualProperty: 'Intellectual Property',
      immigration: 'Immigration',
      personalInjury: 'Personal Injury',
      employment: 'Employment',
      other: 'Other',
    },
    // Documents
    documents: {
      title: 'Documents',
      subtitle: 'Manage case files and legal documents',
      newDocument: 'New Document',
      addNewDocument: 'Add New Document',
      relatedCase: 'Related Case',
      documentType: 'Document Type',
      documentTitle: 'Document Title',
      documentContent: 'Document Content',
      searchDocuments: 'Search documents...',
      noDocuments: 'No documents found',
      createDocument: 'Create Document',
      summarize: 'Summarize',
      aiSummary: 'AI Summary',
      noContent: 'No content',
      view: 'View'
    },
    // Time Tracking
    time: {
      title: 'Time Tracking',
      subtitle: 'Track and manage billable hours',
      manualEntry: 'Manual entry',
      addTimeEntry: 'Add time entry',
      case: 'Case',
      selectCase: 'Select case',
      description: 'Description',
      descriptionPlaceholder: 'Work performed...',
      hours: 'Hours',
      hoursPlaceholder: '1.5',
      rate: 'Rate ($/hr)',
      billable: 'Billable',
      addEntry: 'Add entry',
      totalHours: 'Total hours',
      billableHours: 'Billable hours',
      totalValue: 'Total value',
      recentEntries: 'Recent entries',
      noEntries: 'No time entries yet',
      emptyMessage: 'Start the timer on a case or log hours manually — they\'ll flow straight into the next invoice.',
      configureTimer: 'Configure timer',
      selectCaseTimer: 'Select case for timer',
      workingOn: 'What are you working on?',
      save: 'Save',
      amount: 'Amount',
      loading: 'Loading...',
      unknown: 'Unknown',
      entryAdded: 'Time entry added',
      entryDeleted: 'Time entry deleted',
      entrySaved: 'Time entry saved',
      failedDelete: 'Failed to delete',
      failedSaveTimer: 'Failed to save timer',
      pleaseSelectCase: 'Please select a case first',
      atLeastOneMinute: 'Timer must be at least 1 minute',
      pleaseCaseAndDesc: 'Please select a case and add a description',
      operationFailed: 'Operation failed',
    },
    // Billing (consolidated)
    billing: {
      title: 'Billing',
      subtitle: 'Create and manage invoices',
      newInvoice: 'New Invoice',
      createInvoice: 'Create invoice',
      editInvoice: 'Edit invoice',
      saveChanges: 'Save changes',
      selectCase: 'Select case',
      selectACase: 'Select a case',
      clientName: 'Client name',
      clientEmail: 'Client email',
      lineItems: 'Line items',
      descriptionPlaceholder: 'Description',
      hoursPlaceholder: 'Hours',
      ratePlaceholder: 'Rate',
      addItem: 'Add item',
      dueDate: 'Due date',
      currency: 'Currency',
      currencyDOP: 'DOP - Dominican Pesos (RD$)',
      currencyUSD: 'USD - US Dollars (US$)',
      exchangeRate: 'Exchange rate (USD → DOP)',
      exchangeRateOptional: 'optional',
      exchangeRatePlaceholder: 'e.g. 60.50',
      taxRate: 'ITBIS (%)',
      taxExempt: '0% (exempt)',
      taxStandard: '18% (standard)',
      tax16: '16%',
      taxReduced: '8% (reduced)',
      notes: 'Notes',
      notesPlaceholder: 'Payment terms, thank-you note, etc.',
      total: 'Total',
      totalInvoiced: 'Total invoiced',
      totalPaid: 'Total paid',
      pending: 'Pending',
      invoices: 'Invoices',
      invoice: 'Invoice',
      client: 'Client',
      amount: 'Amount',
      status: 'Status',
      dueDateCol: 'Due date',
      noInvoices: 'No invoices yet',
      emptyMessage: 'Generate your first invoice in seconds — pull time entries from any case and send the PDF by email.',
      loading: 'Loading...',
      preview: 'Preview',
      downloadPdf: 'Download PDF',
      sendByEmail: 'Send by email',
      editAction: 'Edit',
      markAsSent: 'Mark as sent',
      markAsPaid: 'Mark as paid',
      cancelInvoice: 'Cancel',
      cancel: 'Cancel',
      draft: 'Draft',
      sent: 'Sent',
      paid: 'Paid',
      overdue: 'Overdue',
      cancelled: 'Cancelled',
      invoiceCreated: 'Invoice created',
      invoiceUpdated: 'Invoice updated',
      invoiceMarkedAs: 'Invoice marked as',
      failedUpdateStatus: 'Failed to update status',
      errorPdf: 'Error generating PDF',
      errorPreview: 'Error generating preview',
      errorSend: 'Error sending invoice',
      sendPrompt: 'Send invoice to:',
      invoiceSentTo: 'Invoice sent to',
    },
    // Trust/IOLTA (consolidated)
    trust: {
      title: 'Trust/IOLTA',
      subtitle: 'Manage client trust accounts',
      newAccount: 'New account',
      newTransaction: 'Transaction',
      createTrustAccount: 'Create Trust Account',
      recordTransaction: 'Record transaction',
      accountName: 'Account name',
      accountNumber: 'Account number',
      bankName: 'Bank name',
      initialBalance: 'Initial balance',
      account: 'Account',
      transaction: 'Transaction',
      relatedCase: 'Related case (optional)',
      relatedCaseOptional: 'Related case (optional)',
      transactionType: 'Type',
      type: 'Type',
      amount: 'Amount',
      transactionDescription: 'Description',
      totalTrustBalance: 'Total trust balance',
      recentTransactions: 'Recent transactions',
      noAccounts: 'No trust accounts yet',
      noTransactions: 'No transactions yet',
      deposit: 'Deposit',
      withdrawal: 'Withdrawal',
      transfer: 'Transfer',
      balanceAfter: 'Balance after',
      date: 'Date',
      createAccount: 'Create account',
      recordTx: 'Record transaction',
      selectAccount: 'Select account',
      selectCase: 'Select case',
      accountNamePlaceholder: 'Client Trust Account',
      accountNumberPlaceholder: '****1234',
      bankNamePlaceholder: 'First National Bank',
      amountPlaceholder: '0.00',
      notePlaceholder: 'Client retainer deposit',
      accountCreated: 'Trust account created',
      transactionRecorded: 'Transaction recorded',
      unknown: 'Unknown',
    },
    // Tasks
    tasks: {
      title: 'Tasks',
      subtitle: 'Manage your to-do items and deadlines',
      newTask: 'New task',
      createTask: 'Create task',
      taskTitle: 'Task title',
      taskTitlePlaceholder: 'Review contract documents',
      description: 'Description',
      descriptionPlaceholder: 'Additional details...',
      relatedCase: 'Related case',
      selectCase: 'Select case',
      priority: 'Priority',
      dueDate: 'Due date',
      toDo: 'To do',
      inProgress: 'In progress',
      done: 'Done',
      noTasks: 'No tasks',
      low: 'Low',
      medium: 'Medium',
      high: 'High',
      taskCreated: 'Task created',
      taskUpdated: 'Task updated',
      taskDeleted: 'Task deleted',
      failedUpdate: 'Failed to update task',
      failedDelete: 'Failed to delete task',
      operationFailed: 'Operation failed',
    },
    // Calendar
    calendar: {
      title: 'Calendar',
      subtitle: 'Schedule hearings, meetings, and deadlines',
      newEvent: 'New event',
      createEvent: 'Create event',
      eventTitle: 'Event title',
      eventTitlePlaceholder: 'Client meeting',
      eventType: 'Event type',
      relatedCase: 'Related case',
      selectCase: 'Select case',
      startTime: 'Start time',
      endTime: 'End time',
      location: 'Location',
      locationPlaceholder: 'Conference Room A / Zoom',
      description: 'Description',
      descriptionPlaceholder: 'Event details...',
      noEvents: 'No events scheduled',
      upcomingEvents: 'Upcoming events this month',
      noUpcoming: 'No upcoming events',
      meeting: 'Meeting',
      hearing: 'Court hearing',
      deadline: 'Deadline',
      deposition: 'Deposition',
      consultation: 'Consultation',
      eventCreated: 'Event created',
      eventDeleted: 'Event deleted',
      failedDelete: 'Failed to delete event',
      operationFailed: 'Operation failed',
    },
    // Clients
    clients: {
      title: 'Clients',
      subtitle: 'Manage your client contacts',
      addClient: 'Add Client',
      addNewClient: 'Add New Client',
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      company: 'Company',
      address: 'Address',
      notes: 'Notes',
      searchClients: 'Search clients...',
      noClients: 'No clients found',
      added: 'Added',
      contact: 'Contact'
    },
    // Client Portal
    clientPortal: {
      title: 'Client Portal',
      subtitle: 'Secure access for your clients',
      myCases: 'My Cases',
      myInvoices: 'My Invoices',
      myDocuments: 'My Documents',
      welcomeClient: 'Welcome to your Client Portal',
      viewCaseStatus: 'View your case status and updates',
      viewInvoices: 'View and pay your invoices',
      accessDocuments: 'Access shared documents',
      portalDescription: 'This portal allows your clients to securely view their case status, invoices, and shared documents.',
      enablePortal: 'Enable Client Portal',
      portalSettings: 'Portal Settings',
      clientAccess: 'Client Access',
      noCasesAssigned: 'No cases assigned',
      noPendingInvoices: 'No pending invoices',
      noSharedDocuments: 'No shared documents'
    },
    // Audit Log
    audit: {
      title: 'Audit Log',
      subtitle: 'Track all system activities for compliance',
      allResources: 'All Resources',
      created: 'Created',
      updated: 'Updated',
      deleted: 'Deleted',
      total: 'Total',
      action: 'Action',
      user: 'User',
      resource: 'Resource',
      details: 'Details',
      time: 'Time',
      noLogs: 'No audit logs yet'
    },
    // Common
    common: {
      loading: 'Loading...',
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      create: 'Create',
      update: 'Update',
      search: 'Search',
      filter: 'Filter',
      all: 'All',
      yes: 'Yes',
      no: 'No',
      confirm: 'Confirm',
      success: 'Success',
      error: 'Error',
      pleaseWait: 'Please wait...',
      somethingWentWrong: 'Something went wrong',
      amount: 'Amount',
      description: 'Description',
    },
    // Auth-specific placeholders
    authPh: {
      fullNamePlaceholder: 'John Smith',
      firmNamePlaceholder: 'Smith & Associates LLP',
      emailPlaceholder: 'you@lawfirm.com',
      passwordDots: '••••••••',
      mfaCodePlaceholder: '123456',
    },
    // Usage widget + add-ons management
    usage: {
      title: 'Usage this month',
      subtitle: 'AI queries and storage across your firm',
      aiQueries: 'AI queries',
      storage: 'Storage',
      used: 'used',
      remaining: 'remaining',
      of: 'of',
      unlimited: 'Unlimited',
      low: 'You\'re low on capacity',
      lowMsg: 'You have {pct}% left on {resource}. Add a capacity pack so work never stops.',
      alertTitle: 'Capacity running low',
      manageAddons: 'Manage add-ons',
    },
    addons: {
      title: 'Add-ons',
      subtitle: 'Boost your AI queries or storage without changing plans.',
      adminOnly: 'Only tenant admins can manage add-ons.',
      currentAi: 'Current AI pack',
      currentStorage: 'Current storage pack',
      noneActive: 'None',
      selectAi: 'Choose an AI pack',
      selectStorage: 'Choose a storage pack',
      extraQueries: '+{n} queries / month',
      extraStorage: '+{n} GB storage',
      priceMonth: '${p} / month',
      cancel: 'Cancel this pack',
      confirmCancel: 'Cancel this add-on? Extra capacity is removed immediately.',
      activated: 'Add-on activated',
      removed: 'Add-on removed',
      saveFailed: 'Could not update add-on',
      select: 'Select',
      active: 'Active',
    },
    // Footer
    footer: {
      privacy: 'Privacy',
      terms: 'Terms',
      contact: 'Contact',
      copyright: '© 2026 ERP Legal by MSSPRD. All rights reserved.'
    }
  },
  es: {
    // Navigation
    nav: {
      dashboard: 'Panel',
      cases: 'Casos',
      documents: 'Documentos',
      timeTracking: 'Control de Tiempo',
      billing: 'Facturación',
      trust: 'Fideicomiso/IOLTA',
      tasks: 'Tareas',
      calendar: 'Calendario',
      clients: 'Clientes',
      clientPortal: 'Portal de Clientes',
      aiAssistant: 'Asistente IA',
      templates: 'Plantillas',
      integrations: 'Integraciones',
      help: 'Centro de Ayuda',
      team: 'Equipo',
      adminPortal: 'Portal Admin',
      affiliates: 'Afiliados',
      auditLog: 'Registro de Auditoría',
      settings: 'Configuración',
      signOut: 'Cerrar Sesión',
      signIn: 'Iniciar Sesión',
      getStarted: 'Comenzar'
    },
    // Landing Page
    landing: {
      badge: 'Gestión legal para bufetes dominicanos',
      heroTitle: 'Sus horas facturables',
      heroSubtitle: 'merecen algo mejor que una hoja de Excel',
      heroDescription: 'Casos, documentos, facturación, cuentas fiduciarias e investigación legal — por fin en un solo lugar. Diseñado para la forma en que los bufetes dominicanos realmente trabajan.',
      startTrial: 'Inicia prueba de 14 días',
      watchDemo: 'Contactar por WhatsApp',
      lawFirmsTrust: 'bufetes gestionan su práctica con ERP Legal',
      moreBillableHours: 'Más horas facturables',
      fasterRetrieval: 'Encuentre cualquier documento en segundos',
      trustCompliance: 'Cuentas fiduciarias que cuadran',
      reducedAdminTime: 'Menos papeleo, más derecho',
      challengesSolved: '¿Le suena familiar?',
      challengesSubtitle: 'Cada hora que se pierde buscando archivos, cuadrando planillas o reescribiendo el mismo contrato es una hora que no se factura. Estos son los problemas que nos propusimos resolver.',
      everythingNeeds: 'Una plataforma, todas las herramientas',
      everythingSubtitle: 'Desde la apertura del caso hasta el cobro — lo que su equipo usa cada día, sin relleno.',
      simplePricing: 'Un solo plan. Sin sorpresas.',
      pricingSubtitle: 'Tarifa fija por usuario con todo incluido. Agregue capacidad extra solo cuando la necesite.',
      readyTransform: 'Su firma vive del tiempo. Su software también debería.',
      ctaDescription: 'Deje de saltar entre cinco aplicaciones para hacer lo que una sola debería manejar. Casos, calendario, facturación, fideicomiso, IA — un solo lugar de trabajo para su equipo.',
      scheduleDemo: 'Contactar por WhatsApp',
      perMonth: '/mes',
      perUser: 'Por usuario, facturado anualmente',
      contactUs: 'Contáctenos para precios',
      contactSales: 'Contactar Ventas',
      mostPopular: 'Más Popular',
      // Sección Antes/Después
      beforeAfterTitle: 'Su firma hoy vs. con ERP Legal',
      beforeAfterSubtitle: 'Un vistazo a lo que realmente cambia en el día a día.',
      beforeLabel: 'Sin ERP Legal',
      afterLabel: 'Con ERP Legal',
      before1: 'Reescribir el mismo contrato desde cero cada semana',
      after1: 'Plantillas con llenado inteligente — redacte en minutos, no en horas',
      before2: 'Horas facturables en un post-it, la mitad se olvidan',
      after2: 'Un clic inicia el cronómetro, la factura se arma sola',
      before3: 'Clientes escribiendo a las 9 PM "¿alguna novedad con mi caso?"',
      after3: 'Un portal donde ellos mismos consultan el estado',
      before4: 'Plazos repartidos entre cinco calendarios y un grupo de WhatsApp',
      after4: 'Un solo calendario con alertas antes de que se escape algo',
      before5: 'Conciliar las cuentas fiduciarias toma dos días al mes y una aspirina',
      after5: 'Los saldos se actualizan en tiempo real — siempre al centavo',
      before6: '"¿Quién tiene la última versión del escrito?"',
      after6: 'Un archivo, una versión, historial completo',
      // Banda de resultados
      outcomeTitle: 'Resultados que se pueden medir',
      outcomeSubtitle: 'Esto es lo que nos cuentan los bufetes después de sus primeros 90 días.'
    },
    // Features
    features: {
      caseManagement: 'Casos',
      caseDesc: 'Cada caso en un solo lugar — archivos, notas, contactos, historial. Sin buscar entre carpetas.',
      timeTracking: 'Control de tiempo',
      timeDesc: 'Active un cronómetro o registre horas manualmente. El tiempo va directo a la factura.',
      billing: 'Facturación',
      billingDesc: 'Arme facturas desde el tiempo registrado, envíelas por correo, vea quién pagó y quién no.',
      trust: 'Fideicomiso / IOLTA',
      trustDesc: 'Registre cada depósito, retiro y transferencia. Los saldos se concilian automáticamente.',
      calendar: 'Calendario y tareas',
      calendarDesc: 'Audiencias, plazos, reuniones — todo en una vista con alertas para que nada se escape.',
      ai: 'Asistente legal IA',
      aiDesc: 'Haga preguntas sobre legislación dominicana, resuma documentos largos, redacte cláusulas — en segundos.',
      clientPortal: 'Portal de clientes',
      clientPortalDesc: 'Sus clientes entran a ver estado del caso, documentos y facturas sin tener que llamarle.'
    },
    // Challenges
    challenges: {
      scattered: 'Los expedientes viven en WhatsApp, correo y una unidad compartida. Nadie sabe cuál es la versión correcta.',
      scatteredSolution: 'Un solo lugar para cada caso — con historial de versiones para que siempre sepa cuál es la vigente.',
      lostHours: 'Los abogados olvidan registrar el tiempo. Al final del mes, una buena parte del trabajo facturable nunca se cobró.',
      lostHoursSolution: 'Un cronómetro en cada caso. Un toque para empezar, y las horas aparecen en la factura.',
      compliance: 'Conciliación fiduciaria hecha a mano, a un error de Excel de una queja ante el colegio.',
      complianceSolution: 'Los saldos se actualizan con cada transacción. La conciliación pasa en tiempo real, no una vez al mes.',
      deadlines: 'Fechas de audiencia en un calendario, plazos internos en otro. Falle una y tiene un problema.',
      deadlinesSolution: 'Un solo calendario para todo — con alertas que suenan antes de que sea tarde.',
      communication: 'Los clientes llaman a las 9 PM porque no tienen otra forma de saber cómo va su caso.',
      communicationSolution: 'Un portal seguro donde consultan estado, documentos y facturas por su cuenta.',
      manualBilling: 'Armar una factura toma horas y un error puede costarle credibilidad.',
      manualBillingSolution: 'Facturacion de un clic desde los registros de tiempo, exportacion PDF, envio por correo, doble moneda USD/DOP.'
    },
    // Pricing (plan único + add-ons)
    pricing: {
      planName: 'ERP Legal Unificado',
      planTagline: 'Todo lo que necesita su bufete, en una sola tarifa',
      pricePerUser: '$20',
      pricePerUserPerMonth: '/usuario/mes',
      monthly: 'Mensual',
      annual: 'Anual',
      annualSave: 'Ahorre 20%',
      annualBadge: '−20%',
      usersLabel: 'Cantidad de usuarios',
      usersHint: 'Puede agregar o quitar usuarios cuando quiera.',
      basePerUserMonthly: '$20 por usuario / mes',
      basePerUserAnnual: '$192 por usuario / año (20% menos)',
      effectivePerUserMonth: 'Tarifa efectiva',
      subtotalMonthly: 'Total mensual',
      subtotalAnnual: 'Total anual',
      billedMonthly: 'cobro mensual',
      billedAnnually: 'cobro una vez al año',
      youSave: 'Ahorra',
      includedTitle: 'Incluido en cada licencia',
      included: [
        'Usuarios ilimitados',
        'Casos activos ilimitados',
        '500 consultas al asistente IA / mes',
        '6 GB de almacenamiento',
        'Registro de tiempo + facturación',
        'Cuentas fiduciarias (IOLTA)',
        'Gestión de casos, tareas y calendario',
        'Plantillas legales dominicanas',
        'Marca personalizada en facturas',
        'Exportación PDF y Word (.docx)',
        'Multi-moneda (DOP/USD) con historial de tasas',
        'Portal de clientes + 2FA',
        'Soporte prioritario por correo y chat',
      ],
      addonsTitle: 'Crezca bajo demanda con add-ons',
      addonsSubtitle: '¿Necesita más consultas de IA o más espacio? Active solo lo que necesita y cancele cuando quiera.',
      aiAddonsTitle: 'Paquetes de consultas IA',
      storageAddonsTitle: 'Paquetes de almacenamiento',
      aiAddonLabel: '+{n} consultas / mes',
      storageAddonLabel: '+{n} GB de almacenamiento',
      perMonth: '/mes',
      startTrial: 'Iniciar prueba gratis de 14 días',
      cardRequiredNote: 'Se requiere tarjeta para iniciar la prueba. Puede cancelar en cualquier momento antes del día 14 sin cargo.',
    },
    // Auth
    auth: {
      welcomeBack: 'Bienvenido de nuevo',
      createAccount: 'Cree su cuenta',
      enterCredentials: 'Inicie sesión y continúe donde lo dejó',
      startManaging: 'Configure su bufete en menos de dos minutos',
      fullName: 'Nombre Completo',
      lawFirmName: 'Nombre del Bufete',
      email: 'Correo Electrónico',
      password: 'Contraseña',
      signInBtn: 'Iniciar Sesión',
      createAccountBtn: 'Crear Cuenta',
      orContinueWith: 'O continuar con',
      continueGoogle: 'Continuar con Google',
      noAccount: '¿No tiene una cuenta?',
      haveAccount: '¿Ya tiene una cuenta?',
      signUp: 'Registrarse',
      backHome: '← Volver al inicio',
      quote: '"El primer deber de la sociedad es la justicia."',
      quoteAuthor: '— Alexander Hamilton'
    },
    // Dashboard
    dashboard: {
      welcomeBack: 'Bienvenido de nuevo',
      hereIsWhatsHappening: 'Esto es lo que está pasando con su práctica hoy.',
      newCase: 'Nuevo Caso',
      activeCases: 'Casos Activos',
      billableHours: 'Horas Facturables',
      revenueMTD: 'Ingresos del Mes',
      pendingTasks: 'Tareas Pendientes',
      trustBalance: 'Saldo Fiduciario',
      ofTotal: 'de {total} total',
      paid: 'pagado',
      quickActions: 'Acciones Rápidas',
      startTimer: 'Iniciar Cronómetro',
      trackBillable: 'Registrar horas facturables',
      addDocument: 'Agregar Documento',
      uploadFiles: 'Subir archivos de caso',
      createInvoice: 'Crear Factura',
      billClients: 'Facturar a sus clientes',
      addTask: 'Agregar Tarea',
      trackTodo: 'Rastrear pendientes',
      thisMonth: 'Este Mes',
      closedCases: 'Casos Cerrados',
      totalHours: 'Horas Totales',
      draftInvoices: 'Facturas Borrador',
      pendingInvoices: 'Facturas Pendientes',
      completedTasks: 'Tareas Completadas',
      createOrganization: 'Crear Organización',
      welcomeToLegalSuite: '¡Bienvenido a ERP Legal!',
      toGetStarted: 'Para comenzar, cree la organización de su bufete. Este será su espacio de trabajo para gestionar casos, documentos y facturación.',
      createYourOrg: 'Cree su Organización'
    },
    // Cases
    cases: {
      title: 'Casos',
      subtitle: 'Gestione sus casos y asuntos legales',
      newCase: 'Nuevo caso',
      editCase: 'Editar caso',
      createNewCase: 'Crear nuevo caso',
      caseTitle: 'Título del caso',
      caseTitlePlaceholder: 'Pérez vs. Gómez',
      clientName: 'Nombre del cliente',
      clientNamePlaceholder: 'Juan Pérez',
      clientEmail: 'Correo del cliente',
      clientEmailPlaceholder: 'cliente@correo.com',
      caseType: 'Tipo de caso',
      selectType: 'Seleccione tipo',
      status: 'Estado',
      description: 'Descripción',
      descriptionPlaceholder: 'Detalles y notas del caso...',
      searchCases: 'Buscar casos...',
      allStatus: 'Todos los estados',
      open: 'Abierto',
      pending: 'Pendiente',
      closed: 'Cerrado',
      archived: 'Archivado',
      noCases: 'Aún no tiene casos',
      emptyMessage: 'Abra su primer caso para comenzar a centralizar clientes, documentos, tiempo y facturas en un solo lugar.',
      noMatchesTitle: 'Ningún caso coincide con los filtros',
      noMatchesMessage: 'Pruebe ajustando la búsqueda o el filtro de estado.',
      clearFilters: 'Limpiar filtros',
      errTitleShort: 'Escriba al menos 3 caracteres.',
      errClientRequired: 'El nombre del cliente es obligatorio.',
      errEmailInvalid: 'Ingrese un correo válido.',
      errTypeRequired: 'Seleccione un tipo de caso.',
      createCase: 'Crear caso',
      updateCase: 'Actualizar caso',
      case: 'Caso',
      client: 'Cliente',
      type: 'Tipo',
      date: 'Fecha',
      caseCreated: 'Caso creado',
      caseUpdated: 'Caso actualizado',
      caseDeleted: 'Caso eliminado',
      confirmDelete: '¿Está seguro que desea eliminar este caso?',
      deleteFailed: 'No se pudo eliminar el caso',
      operationFailed: 'Operación fallida',
      // Tipos de caso
      civilLitigation: 'Litigio civil',
      criminalDefense: 'Defensa penal',
      familyLaw: 'Derecho de familia',
      corporate: 'Corporativo',
      realEstate: 'Inmobiliario',
      intellectualProperty: 'Propiedad intelectual',
      immigration: 'Migración',
      personalInjury: 'Lesiones personales',
      employment: 'Laboral',
      other: 'Otro',
    },
    // Documents
    documents: {
      title: 'Documentos',
      subtitle: 'Gestione archivos de casos y documentos legales',
      newDocument: 'Nuevo Documento',
      addNewDocument: 'Agregar Nuevo Documento',
      relatedCase: 'Caso Relacionado',
      documentType: 'Tipo de Documento',
      documentTitle: 'Título del Documento',
      documentContent: 'Contenido del Documento',
      searchDocuments: 'Buscar documentos...',
      noDocuments: 'No se encontraron documentos',
      createDocument: 'Crear Documento',
      summarize: 'Resumir',
      aiSummary: 'Resumen IA',
      noContent: 'Sin contenido',
      view: 'Ver'
    },
    // Time Tracking
    time: {
      title: 'Control de Tiempo',
      subtitle: 'Registre y gestione horas facturables',
      manualEntry: 'Entrada manual',
      addTimeEntry: 'Agregar entrada de tiempo',
      case: 'Caso',
      selectCase: 'Seleccione caso',
      description: 'Descripción',
      descriptionPlaceholder: 'Trabajo realizado...',
      hours: 'Horas',
      hoursPlaceholder: '1.5',
      rate: 'Tarifa ($/hr)',
      billable: 'Facturable',
      addEntry: 'Agregar entrada',
      totalHours: 'Horas totales',
      billableHours: 'Horas facturables',
      totalValue: 'Valor total',
      recentEntries: 'Entradas recientes',
      noEntries: 'No hay entradas de tiempo aún',
      emptyMessage: 'Inicie el cronómetro en un caso o registre horas manualmente — pasan directo a la siguiente factura.',
      configureTimer: 'Configurar cronómetro',
      selectCaseTimer: 'Seleccione caso para el cronómetro',
      workingOn: '¿En qué está trabajando?',
      save: 'Guardar',
      amount: 'Monto',
      loading: 'Cargando...',
      unknown: 'Desconocido',
      entryAdded: 'Entrada de tiempo agregada',
      entryDeleted: 'Entrada eliminada',
      entrySaved: 'Entrada de tiempo guardada',
      failedDelete: 'No se pudo eliminar',
      failedSaveTimer: 'No se pudo guardar el cronómetro',
      pleaseSelectCase: 'Seleccione un caso primero',
      atLeastOneMinute: 'El cronómetro debe durar al menos 1 minuto',
      pleaseCaseAndDesc: 'Seleccione un caso y agregue una descripción',
      operationFailed: 'Operación fallida',
    },
    // Billing (consolidado)
    billing: {
      title: 'Facturación',
      subtitle: 'Cree y gestione facturas',
      newInvoice: 'Nueva factura',
      createInvoice: 'Crear factura',
      editInvoice: 'Editar factura',
      saveChanges: 'Guardar cambios',
      selectCase: 'Seleccione caso',
      selectACase: 'Seleccione un caso',
      clientName: 'Nombre del cliente',
      clientEmail: 'Correo del cliente',
      lineItems: 'Partidas',
      descriptionPlaceholder: 'Descripción',
      hoursPlaceholder: 'Horas',
      ratePlaceholder: 'Tarifa',
      addItem: 'Agregar partida',
      dueDate: 'Fecha de vencimiento',
      currency: 'Moneda',
      currencyDOP: 'DOP - Pesos Dominicanos (RD$)',
      currencyUSD: 'USD - Dólares (US$)',
      exchangeRate: 'Tasa de cambio (USD → DOP)',
      exchangeRateOptional: 'opcional',
      exchangeRatePlaceholder: 'p. ej. 60.50',
      taxRate: 'ITBIS (%)',
      taxExempt: '0% (exento)',
      taxStandard: '18% (estándar)',
      tax16: '16%',
      taxReduced: '8% (reducido)',
      notes: 'Notas',
      notesPlaceholder: 'Términos de pago, nota de agradecimiento, etc.',
      total: 'Total',
      totalInvoiced: 'Total facturado',
      totalPaid: 'Total pagado',
      pending: 'Pendiente',
      invoices: 'Facturas',
      invoice: 'Factura',
      client: 'Cliente',
      amount: 'Monto',
      status: 'Estado',
      dueDateCol: 'Vencimiento',
      noInvoices: 'No hay facturas aún',
      emptyMessage: 'Genere su primera factura en segundos — traiga las horas registradas de cualquier caso y envíe el PDF por correo.',
      loading: 'Cargando...',
      preview: 'Vista previa',
      downloadPdf: 'Descargar PDF',
      sendByEmail: 'Enviar por correo',
      editAction: 'Editar',
      markAsSent: 'Marcar como enviada',
      markAsPaid: 'Marcar como pagada',
      cancelInvoice: 'Cancelar',
      cancel: 'Cancelar',
      draft: 'Borrador',
      sent: 'Enviada',
      paid: 'Pagada',
      overdue: 'Vencida',
      cancelled: 'Cancelada',
      invoiceCreated: 'Factura creada',
      invoiceUpdated: 'Factura actualizada',
      invoiceMarkedAs: 'Factura marcada como',
      failedUpdateStatus: 'No se pudo actualizar el estado',
      errorPdf: 'Error generando PDF',
      errorPreview: 'Error generando vista previa',
      errorSend: 'Error enviando factura',
      sendPrompt: 'Enviar factura a:',
      invoiceSentTo: 'Factura enviada a',
    },
    // Trust/IOLTA (consolidado)
    trust: {
      title: 'Fideicomiso/IOLTA',
      subtitle: 'Gestione las cuentas fiduciarias de sus clientes',
      newAccount: 'Nueva cuenta',
      newTransaction: 'Transacción',
      createTrustAccount: 'Crear cuenta fiduciaria',
      recordTransaction: 'Registrar transacción',
      accountName: 'Nombre de la cuenta',
      accountNumber: 'Número de cuenta',
      bankName: 'Banco',
      initialBalance: 'Saldo inicial',
      account: 'Cuenta',
      transaction: 'Transacción',
      relatedCase: 'Caso relacionado (opcional)',
      relatedCaseOptional: 'Caso relacionado (opcional)',
      transactionType: 'Tipo',
      type: 'Tipo',
      amount: 'Monto',
      transactionDescription: 'Descripción',
      totalTrustBalance: 'Saldo fiduciario total',
      recentTransactions: 'Transacciones recientes',
      noAccounts: 'No hay cuentas fiduciarias aún',
      noTransactions: 'No hay transacciones aún',
      deposit: 'Depósito',
      withdrawal: 'Retiro',
      transfer: 'Transferencia',
      balanceAfter: 'Saldo después',
      date: 'Fecha',
      createAccount: 'Crear cuenta',
      recordTx: 'Registrar transacción',
      selectAccount: 'Seleccione cuenta',
      selectCase: 'Seleccione caso',
      accountNamePlaceholder: 'Cuenta Fiduciaria del Cliente',
      accountNumberPlaceholder: '****1234',
      bankNamePlaceholder: 'Nombre del banco',
      amountPlaceholder: '0.00',
      notePlaceholder: 'Depósito del retainer del cliente',
      accountCreated: 'Cuenta fiduciaria creada',
      transactionRecorded: 'Transacción registrada',
      unknown: 'Desconocido',
    },
    // Tasks
    tasks: {
      title: 'Tareas',
      subtitle: 'Gestione sus pendientes y fechas límite',
      newTask: 'Nueva tarea',
      createTask: 'Crear tarea',
      taskTitle: 'Título de la tarea',
      taskTitlePlaceholder: 'Revisar contrato',
      description: 'Descripción',
      descriptionPlaceholder: 'Detalles adicionales...',
      relatedCase: 'Caso relacionado',
      selectCase: 'Seleccione caso',
      priority: 'Prioridad',
      dueDate: 'Fecha límite',
      toDo: 'Por hacer',
      inProgress: 'En progreso',
      done: 'Completado',
      noTasks: 'Sin tareas',
      low: 'Baja',
      medium: 'Media',
      high: 'Alta',
      taskCreated: 'Tarea creada',
      taskUpdated: 'Tarea actualizada',
      taskDeleted: 'Tarea eliminada',
      failedUpdate: 'No se pudo actualizar la tarea',
      failedDelete: 'No se pudo eliminar la tarea',
      operationFailed: 'Operación fallida',
    },
    // Calendar
    calendar: {
      title: 'Calendario',
      subtitle: 'Programe audiencias, reuniones y fechas límite',
      newEvent: 'Nuevo evento',
      createEvent: 'Crear evento',
      eventTitle: 'Título del evento',
      eventTitlePlaceholder: 'Reunión con cliente',
      eventType: 'Tipo de evento',
      relatedCase: 'Caso relacionado',
      selectCase: 'Seleccione caso',
      startTime: 'Hora de inicio',
      endTime: 'Hora de fin',
      location: 'Ubicación',
      locationPlaceholder: 'Sala de conferencias A / Zoom',
      description: 'Descripción',
      descriptionPlaceholder: 'Detalles del evento...',
      noEvents: 'No hay eventos programados',
      upcomingEvents: 'Próximos eventos este mes',
      noUpcoming: 'No hay próximos eventos',
      meeting: 'Reunión',
      hearing: 'Audiencia judicial',
      deadline: 'Fecha límite',
      deposition: 'Deposición',
      consultation: 'Consulta',
      eventCreated: 'Evento creado',
      eventDeleted: 'Evento eliminado',
      failedDelete: 'No se pudo eliminar el evento',
      operationFailed: 'Operación fallida',
    },
    // Clients
    clients: {
      title: 'Clientes',
      subtitle: 'Gestione sus contactos de clientes',
      addClient: 'Agregar Cliente',
      addNewClient: 'Agregar Nuevo Cliente',
      name: 'Nombre',
      email: 'Email',
      phone: 'Teléfono',
      company: 'Empresa',
      address: 'Dirección',
      notes: 'Notas',
      searchClients: 'Buscar clientes...',
      noClients: 'No se encontraron clientes',
      added: 'Agregado',
      contact: 'Contacto'
    },
    // Client Portal
    clientPortal: {
      title: 'Portal de Clientes',
      subtitle: 'Acceso seguro para sus clientes',
      myCases: 'Mis Casos',
      myInvoices: 'Mis Facturas',
      myDocuments: 'Mis Documentos',
      welcomeClient: 'Bienvenido a su Portal de Cliente',
      viewCaseStatus: 'Ver el estado de sus casos y actualizaciones',
      viewInvoices: 'Ver y pagar sus facturas',
      accessDocuments: 'Acceder a documentos compartidos',
      portalDescription: 'Este portal permite a sus clientes ver de forma segura el estado de sus casos, facturas y documentos compartidos.',
      enablePortal: 'Habilitar Portal de Clientes',
      portalSettings: 'Configuración del Portal',
      clientAccess: 'Acceso de Clientes',
      noCasesAssigned: 'No hay casos asignados',
      noPendingInvoices: 'No hay facturas pendientes',
      noSharedDocuments: 'No hay documentos compartidos'
    },
    // Audit Log
    audit: {
      title: 'Registro de Auditoría',
      subtitle: 'Rastree todas las actividades del sistema para cumplimiento',
      allResources: 'Todos los Recursos',
      created: 'Creado',
      updated: 'Actualizado',
      deleted: 'Eliminado',
      total: 'Total',
      action: 'Acción',
      user: 'Usuario',
      resource: 'Recurso',
      details: 'Detalles',
      time: 'Hora',
      noLogs: 'No hay registros de auditoría aún'
    },
    // Common
    common: {
      loading: 'Cargando...',
      save: 'Guardar',
      cancel: 'Cancelar',
      delete: 'Eliminar',
      edit: 'Editar',
      create: 'Crear',
      update: 'Actualizar',
      search: 'Buscar',
      filter: 'Filtrar',
      all: 'Todos',
      yes: 'Si',
      no: 'No',
      confirm: 'Confirmar',
      success: 'Exito',
      error: 'Error',
      pleaseWait: 'Por favor espere...',
      somethingWentWrong: 'Algo salio mal',
      amount: 'Monto',
      description: 'Descripcion',
    },
    // Auth-specific placeholders
    authPh: {
      fullNamePlaceholder: 'Juan Perez',
      firmNamePlaceholder: 'Perez & Asociados SRL',
      emailPlaceholder: 'correo@sufirma.com',
      passwordDots: '••••••••',
      mfaCodePlaceholder: '123456',
    },
    // Widget de uso y gestión de add-ons
    usage: {
      title: 'Uso este mes',
      subtitle: 'Consultas de IA y almacenamiento de su bufete',
      aiQueries: 'Consultas IA',
      storage: 'Almacenamiento',
      used: 'usado',
      remaining: 'restante',
      of: 'de',
      unlimited: 'Ilimitado',
      low: 'Le queda poca capacidad',
      lowMsg: 'Le queda {pct}% en {resource}. Active un paquete adicional para no detener el trabajo.',
      alertTitle: 'Capacidad por agotarse',
      manageAddons: 'Gestionar add-ons',
    },
    addons: {
      title: 'Add-ons',
      subtitle: 'Aumente sus consultas de IA o su almacenamiento sin cambiar de plan.',
      adminOnly: 'Solo los administradores del bufete pueden gestionar los add-ons.',
      currentAi: 'Paquete IA activo',
      currentStorage: 'Paquete de almacenamiento activo',
      noneActive: 'Ninguno',
      selectAi: 'Elija un paquete de IA',
      selectStorage: 'Elija un paquete de almacenamiento',
      extraQueries: '+{n} consultas / mes',
      extraStorage: '+{n} GB de almacenamiento',
      priceMonth: '${p} / mes',
      cancel: 'Cancelar este paquete',
      confirmCancel: '¿Cancelar este add-on? La capacidad extra se elimina de inmediato.',
      activated: 'Add-on activado',
      removed: 'Add-on eliminado',
      saveFailed: 'No se pudo actualizar el add-on',
      select: 'Seleccionar',
      active: 'Activo',
    },
    // Footer
    footer: {
      privacy: 'Privacidad',
      terms: 'Términos',
      contact: 'Contacto',
      copyright: '© 2026 ERP Legal by MSSPRD. Todos los derechos reservados.'
    }
  }
};

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
    // a11y: keep <html lang="..."> in sync so screen readers pronounce
    // the current page in the correct language instead of defaulting
    // to the browser's locale.
    if (typeof document !== 'undefined' && document.documentElement) {
      document.documentElement.lang = language;
    }
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'es' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
